import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BellRing, Users } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useSimpleRole } from "@/hooks/use-simple-role";

export type AlertLevel = "off" | "alerts" | "all";

interface Props {
  deviceId: string;
  device: any;
  updateField: (field: string, value: string | number) => Promise<void>;
  onUpdate: () => Promise<void>;
}

const db = supabase as any;

export const LevelSelect = ({ value, onChange, disabled }: { value: AlertLevel; onChange: (v: AlertLevel) => void; disabled?: boolean }) => (
  <Select value={value} onValueChange={(v) => onChange(v as AlertLevel)} disabled={disabled}>
    <SelectTrigger className="h-8 w-[140px]"><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="off">Off</SelectItem>
      <SelectItem value="alerts">Alerts only</SelectItem>
      <SelectItem value="all">All images</SelectItem>
    </SelectContent>
  </Select>
);

export async function setAlertLevel(userId: string, deviceId: string, level: AlertLevel, createdBy?: string) {
  if (level === "off") {
    const { error } = await db.from("device_alert_subscriptions").delete().eq("user_id", userId).eq("device_id", deviceId).eq("channel", "email");
    if (error) throw error;
    return;
  }
  const { error } = await db.from("device_alert_subscriptions").upsert(
    { user_id: userId, device_id: deviceId, channel: "email", level, created_by: createdBy ?? userId, updated_at: new Date().toISOString() },
    { onConflict: "user_id,device_id,channel" },
  );
  if (error) throw error;
}

// Switches the camera between legacy BuildShip emails and app-managed alerts,
// preserving the old email list so it can be restored when switched back off.
export async function toggleAppAlerts(
  deviceId: string,
  device: any,
  on: boolean,
  updateField: (field: string, value: string | number) => Promise<void>,
  onUpdate: () => Promise<void>,
) {
  const legacy = on ? (device?.notification_email || null) : null;
  const { data: settingsResult, error: settingsError } = await supabase.functions.invoke("device-alert-settings", {
    body: { deviceId, enabled: on, legacyEmail: legacy },
  });
  if (settingsError) throw settingsError;
  if (!settingsResult?.success) throw new Error(settingsResult?.error || "Could not update camera alert settings");

  if (on) {
    // Stop BuildShip emailing the old address list for this camera
    if (legacy) await updateField("notificationEmail", "");
  } else {
    const savedLegacy = settingsResult.settings?.legacy_notification_email || device?.legacy_notification_email || "";
    if (savedLegacy) await updateField("notificationEmail", savedLegacy);
  }
  await onUpdate();
  toast.success(on ? "Alerts now sent from the app" : "Switched back to the old notification email");
}

// Subscribers UI without its own card wrapper or the "Send alerts from the app"
// switch — embedded inside the Notifications card on the AI Setup tab.
export const AlertSubscribersContent = ({ deviceId, device }: { deviceId: string; device: any }) => {
  const { user } = useAuth();
  const { role, isSystemAdmin } = useSimpleRole();
  const canManage = role === "admin" || isSystemAdmin;
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const appAlertsOn = device?.app_alerts_enabled === true;

  const { data: subs = [], error: subsError } = useQuery({
    queryKey: ["device-alert-subs", deviceId],
    queryFn: async () => {
      const { data, error } = await db.from("device_alert_subscriptions").select("user_id, level").eq("device_id", deviceId).eq("channel", "email");
      if (error) throw error;
      return data as { user_id: string; level: AlertLevel }[];
    },
    retry: false,
  });

  const { data: people = [] } = useQuery({
    queryKey: ["device-alert-candidates", deviceId],
    enabled: canManage,
    queryFn: async () => {
      const { data, error } = await db.rpc("list_device_alert_candidates", { _device_id: deviceId });
      if (error) throw error;
      return data as { user_id: string; first_name: string | null; last_name: string | null; email: string | null; organization_name: string | null }[];
    },
    retry: false,
  });

  const levelFor = (uid?: string): AlertLevel => (subs.find((s) => s.user_id === uid)?.level as AlertLevel) || "off";

  const change = async (uid: string, level: AlertLevel) => {
    setBusy(true);
    try {
      await setAlertLevel(uid, deviceId, level, user?.id);
      await qc.invalidateQueries({ queryKey: ["device-alert-subs", deviceId] });
      await qc.invalidateQueries({ queryKey: ["my-camera-alerts"] });
      toast.success(level === "off" ? "Alerts turned off" : "Alert preference saved");
    } catch (e: any) {
      toast.error(`Couldn't save alert preference: ${e?.message || "unknown error"}`);
    } finally { setBusy(false); }
  };

  const subscribed = useMemo(() => {
    const known = people.filter((p) => p.user_id !== user?.id && levelFor(p.user_id) !== "off");
    const knownIds = new Set(people.map((p) => p.user_id));
    const unknown = subs
      .filter((s) => s.user_id !== user?.id && !knownIds.has(s.user_id))
      .map((s) => ({ user_id: s.user_id, first_name: null, last_name: null, email: null, organization_name: null }));
    return [...known, ...unknown];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [people, subs, user?.id]);

  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return people
      .filter((p) => p.user_id !== user?.id && levelFor(p.user_id) === "off")
      .filter((p) => `${p.first_name ?? ""} ${p.last_name ?? ""} ${p.email ?? ""}`.toLowerCase().includes(q))
      .slice(0, 8);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [people, subs, search, user?.id]);

  const nameOf = (p: { first_name: string | null; last_name: string | null; email: string | null }) =>
    [p.first_name, p.last_name].filter(Boolean).join(" ") || p.email || "Unknown user";

  if (subsError) {
    return (
      <p className="text-sm text-muted-foreground">
        Camera alert subscriptions aren't set up yet.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        {appAlertsOn
          ? "Emails for this camera now go to the people subscribed below."
          : "Not active yet — this camera still uses the Notification Email below. Subscriptions you set now start working when an admin switches on \"Enable Advanced Notifications\"."}
      </p>

      {user && (
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label className="text-sm">Me</Label>
            <p className="text-xs text-muted-foreground">{user.email}</p>
          </div>
          <LevelSelect value={levelFor(user.id)} onChange={(v) => change(user.id, v)} disabled={busy} />
        </div>
      )}

      {canManage && (
        <div className="space-y-3 border-t pt-3">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-brand-500" />
            <Label className="text-sm">People getting alerts</Label>
          </div>
          <div className="divide-y">
            {subscribed.map((p) => (
              <div key={p.user_id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm truncate">{nameOf(p)}</p>
                  <p className="text-xs text-muted-foreground truncate">{p.email}{p.organization_name ? ` · ${p.organization_name}` : ""}</p>
                </div>
                <LevelSelect value={levelFor(p.user_id)} onChange={(v) => change(p.user_id, v)} disabled={busy} />
              </div>
            ))}
            {subscribed.length === 0 && <p className="text-xs text-muted-foreground py-2">No one else gets alerts from this camera yet.</p>}
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Add people</Label>
            <Input className="h-8" placeholder="Search by name or email" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search.trim() && (
              <div className="divide-y rounded-md border">
                {searchResults.map((p) => (
                  <div key={p.user_id} className="flex items-center justify-between gap-3 px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-sm truncate">{nameOf(p)}</p>
                      <p className="text-xs text-muted-foreground truncate">{p.email}{p.organization_name ? ` · ${p.organization_name}` : ""}</p>
                    </div>
                    <Button size="sm" variant="outline" disabled={busy} onClick={async () => { await change(p.user_id, "alerts"); setSearch(""); }}>
                      Add
                    </Button>
                  </div>
                ))}
                {searchResults.length === 0 && <p className="text-xs text-muted-foreground px-3 py-2">No matching people.</p>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Legacy standalone card wrapper — kept for backwards compatibility; the AI
// Setup tab now embeds AlertSubscribersContent in the Notifications card.
export const DeviceAlertSubscribers = ({ deviceId, device, updateField, onUpdate }: Props) => {
  const { role, isSystemAdmin } = useSimpleRole();
  const canManage = role === "admin" || isSystemAdmin;
  const [busy, setBusy] = useState(false);
  const appAlertsOn = device?.app_alerts_enabled === true;

  const handleToggle = async (on: boolean) => {
    setBusy(true);
    try {
      await toggleAppAlerts(deviceId, device, on, updateField, onUpdate);
    } catch (e: any) {
      toast.error(`Couldn't change alert sending: ${e?.message || "unknown error"}`);
    } finally { setBusy(false); }
  };

  return (
    <Card className="border bg-muted/50">
      <CardContent className="py-4 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <BellRing className="h-4 w-4 text-brand-500" />
            <span className="text-sm font-semibold">Alert subscribers</span>
          </div>
          {canManage && (
            <div className="flex items-center gap-2">
              <Label className="text-sm">Enable Advanced Notifications</Label>
              <Switch checked={appAlertsOn} onCheckedChange={handleToggle} disabled={busy} />
            </div>
          )}
        </div>

        <AlertSubscribersContent deviceId={deviceId} device={device} />
      </CardContent>
    </Card>
  );
};
